import { maxBy } from 'es-toolkit'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Row, Button, Col, Card, CardBody, CardTitle } from 'reactstrap'

import { COLOR_TYPES } from 'api/constants'
import type { ColorType } from 'api/types'

import { getGroupList, getGroup, selectGroupsStatus, updateGroup, createGroup } from 'slices/groupsSlice'
import { showError, showSuccess } from 'slices/notificationSlice'
import { ERROR_STATUS_CODE, ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'
import { getWorkspace, selectWorkspacesStatus } from 'slices/workspacesSlice'

import {
  NavMenu,
  List,
  InputFormat,
  CustomButton,
  ColorPickerFormat,
  CardSubmitFooter,
  BadgeLabel,
} from 'components/common'
import * as Rules from 'components/common/FormFormat/ValidationRules'
import { COLUMN_SIZES } from 'components/common/constants'
import type { ListItem } from 'components/common/types'

import useAuthority from 'hooks/useAuthority'

import placeholderImage from 'images/allEmpty.svg'

import GroupDelete from './GroupDelete'

import styles from './GroupEdit.module.scss'

const NEW_GROUP_ID = 0

const GroupEdit = () => {
  const params = useParams<'workspaceId'>()
  const workspaceId = Number(params.workspaceId)

  const [groupNameValidity, setGroupNameValidity] = useState(false)
  const [groupName, setGroupName] = useState<string | undefined>(undefined)
  const [groupColor, setGroupColor] = useState<ColorType>(COLOR_TYPES.SILVER)
  const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined)
  const [groupItems, setGroupItems] = useState<ListItem[]>([])
  const [openDelete, setOpenDelete] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const dispatch = useDispatch()

  const isNewItem = useMemo(() => selectedGroupId === NEW_GROUP_ID, [selectedGroupId])

  useEffect(() => {
    dispatch(getGroupList(workspaceId))
    dispatch(getWorkspace(workspaceId))
  }, [dispatch, workspaceId])

  const { groups, group, groupWorkspaceId, isRequesting, errorMessage } = useSelector(selectGroupsStatus, shallowEqual)
  const { workspace } = useSelector(selectWorkspacesStatus, shallowEqual)

  const { isReadOnlyWorkspace } = useAuthority(workspaceId)

  const hasGroupItems = useMemo(() => groupItems.length > 0, [groupItems])

  const initGroupItems = useCallback(() => {
    // 前WSのgroupsが残っているため、workspaceIdとgroupWorkspaceIdが不一致の間はsetGroupItemsしない
    if (workspaceId !== groupWorkspaceId) {
      return
    }
    setGroupItems(
      groups.map(data => ({
        id: data.id,
        title: data.name || '',
        color: data.color,
        option: <div className={`${styles.square} bg-${data.color}`} />,
      }))
    )
  }, [groups, workspaceId, groupWorkspaceId])

  useEffect(() => {
    if (selectedGroupId === NEW_GROUP_ID) {
      setGroupName('')
      setGroupColor(COLOR_TYPES.SILVER)
      return
    }
    if (!selectedGroupId) {
      return
    }
    dispatch(getGroup(workspaceId, selectedGroupId))
  }, [selectedGroupId, workspaceId, dispatch])

  useEffect(() => {
    if (!group) {
      return
    }
    setGroupName(group.name)
    setGroupColor(group.color)
  }, [group])

  useEffect(() => {
    initGroupItems()
  }, [initGroupItems])

  useEffect(() => {
    if (groupItems.length === 0) {
      return
    }

    setSelectedGroupId(prev => {
      // 追加アイテムを保存した時
      if (prev === NEW_GROUP_ID && !groupItems.some(item => item.id === NEW_GROUP_ID)) {
        return Number(maxBy(groupItems, item => item.id)!.id)
      }
      // 初期化時とアイテム削除時
      if (prev === undefined || !groupItems.some(item => item.id === prev)) {
        return Number(groupItems[0].id)
      }
      return prev
    })
  }, [groupItems, selectedGroupId])

  const onDetailClick = () => {
    window.open('https://help.smileboard.jp/create_group', '_blank')
  }

  const disabled = useMemo(
    () => !(groupName && groupNameValidity && groupColor),
    [groupName, groupNameValidity, groupColor]
  )

  const unchanged = useMemo(
    () => groupName === group?.name && groupColor === group?.color,
    [groupName, groupColor, group]
  )

  const onSubmit = useCallback(() => {
    if (selectedGroupId === NEW_GROUP_ID && groupName && groupColor) {
      dispatch(createGroup(workspaceId, groupName, groupColor))
      setSubmitted(true)
      return
    }
    if (selectedGroupId && groupName) {
      dispatch(updateGroup(workspaceId, selectedGroupId, groupName, groupColor))
      setSubmitted(true)
    }
  }, [dispatch, workspaceId, selectedGroupId, groupName, groupColor])

  const onCancel = useCallback(() => {
    // グループ追加でのキャンセル
    if (selectedGroupId === NEW_GROUP_ID) {
      setGroupItems(prev => prev.filter(item => item.id !== NEW_GROUP_ID))
      const newId = groups.length > 0 && groups[0].id
      setSelectedGroupId(newId || undefined)
      return
    }
    // グループ編集でのキャンセル
    if (!group) {
      return
    }
    setSelectedGroupId(group.id)
    setGroupName(group.name)
    setGroupColor(group.color)
  }, [group, groups, selectedGroupId])

  const onAddNewItem = useCallback(() => {
    setGroupItems(prev =>
      prev.concat({
        title: '',
        id: NEW_GROUP_ID,
        color: COLOR_TYPES.SILVER,
        option: <div className={`${styles.square} bg-${COLOR_TYPES.SILVER}`} />,
      })
    )
    setSelectedGroupId(NEW_GROUP_ID)
  }, [])

  const handleListItemChange = useCallback((id: number) => {
    setSelectedGroupId(prevGroupId => {
      if (prevGroupId === id) {
        return prevGroupId
      }
      if (prevGroupId === NEW_GROUP_ID) {
        setGroupItems(prev => prev.filter(item => item.id !== NEW_GROUP_ID))
      }

      return id
    })
  }, [])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(showSuccess())
    } else {
      if (errorMessage === ERROR_STATUS_CODE.CONFLICT) {
        dispatch(showError({ errorMessage: 'すでにこの名前のグループは存在しています。' }))
      } else if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        // ENABLE_DIALOGのときにはエラーダイアログが出るのでNotificationは出さない
        dispatch(showError())
      }
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, dispatch])

  return (
    <NavMenu>
      <div className="mt-3 mx-3">
        <div className="mb-3">
          <div className="d-flex align-items-center text-nowrap">
            <div className="font-x-large fw-bold">グループ管理</div>
            <div className="px-2 text-truncate">{workspace && <BadgeLabel label={workspace?.name || ''} />}</div>
            <CustomButton
              icon="plus"
              className="ms-auto"
              onClick={onAddNewItem}
              disabled={groupItems.some(item => item.id === NEW_GROUP_ID)}
            >
              グループの追加
            </CustomButton>
          </div>
        </div>
        <Row className={styles.row}>
          <Col md={4} className="h-100">
            <Card className={styles.list}>
              {hasGroupItems ? (
                <List items={groupItems} selectedId={selectedGroupId} onAction={handleListItemChange} />
              ) : (
                <CardBody className="d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <img className={`mx-auto d-block w-100 ${styles.placeholderImage}`} src={placeholderImage} alt="" />
                    <div className="font-middle fw-bold py-4">グループがまだ登録されていません</div>
                    <div>
                      ワークスペース内のメンバーをグループ、熟練度などでまとめて管理する事ができます。グループに属する作業者に一括で作業を予定したり、グループ毎の生産性を見ることもできます。
                    </div>
                    <Button className="mx-auto d-block m-4" size="sm" outline onClick={onDetailClick}>
                      グループについてもっと詳しく
                    </Button>
                  </div>
                </CardBody>
              )}
            </Card>
          </Col>
          <Col md={8} className="h-100">
            <Card className="h-100">
              {hasGroupItems ? (
                <>
                  <div className="h-100 overflow-auto">
                    <CardBody>
                      <div className="d-flex justify-content-between">
                        <CardTitle className="font-large fw-bold">グループ情報</CardTitle>
                        <span className="font-x-small text-muted">※必須項目</span>
                      </div>
                      <InputFormat
                        label="名前※"
                        placeholder="名前を入力"
                        value={groupName}
                        validations={[Rules.Required]}
                        maxLength={100}
                        size={COLUMN_SIZES.MIDDLE}
                        onChange={value => setGroupName(value)}
                        onValidate={setGroupNameValidity}
                        className="mb-3"
                      />
                      <ColorPickerFormat
                        label="グループカラー"
                        color={groupColor}
                        size={COLUMN_SIZES.MIDDLE}
                        onChange={setGroupColor}
                      />
                    </CardBody>

                    <CardBody>
                      <CardTitle className="font-large fw-bold">グループの削除</CardTitle>
                      <Button
                        outline
                        color="danger"
                        className="my-3"
                        onClick={() => setOpenDelete(true)}
                        disabled={selectedGroupId === NEW_GROUP_ID || isReadOnlyWorkspace}
                      >
                        このグループを削除
                      </Button>
                    </CardBody>
                  </div>
                  <CardSubmitFooter
                    onCancel={() => onCancel()}
                    onSubmit={onSubmit}
                    submitDisabled={disabled || unchanged || isReadOnlyWorkspace}
                    cancelDisabled={unchanged}
                    updatedBy={isNewItem ? undefined : group?.updatedByName}
                    updatedAt={isNewItem ? undefined : group?.updatedAt}
                  />
                </>
              ) : (
                <CardBody className="d-flex align-items-center justify-content-center">
                  <div className="text-center">
                    <img className={`mx-auto d-block ${styles.placeholderImage}`} src={placeholderImage} alt="" />
                    <div className="font-middle fw-bold py-4">グループが選択されていません</div>
                    <div>グループを選択して、詳細情報を編集しましょう。</div>
                  </div>
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>

        <GroupDelete
          isOpen={openDelete}
          workspaceId={workspaceId}
          groupId={selectedGroupId}
          onSuccess={() => {
            dispatch(showSuccess())
            setOpenDelete(false)
          }}
          onCancel={() => setOpenDelete(false)}
        />
      </div>
    </NavMenu>
  )
}

export default GroupEdit
